import { useState, useEffect, useRef, useCallback } from "react";
import {
  BarChart3, Search, Upload, Link2, LogOut, Lock, Eye, EyeOff,
  ExternalLink, CheckCircle, AlertCircle, X, RefreshCw, FileImage,
  Globe, Settings, Image, FileText, Package, Plus, Pencil, Trash2,
  ChevronLeft, Copy, ToggleLeft, ToggleRight, Phone, Layout,
} from "lucide-react";

// ── Config ────────────────────────────────────────────────────────────────
// Change ADMIN_PASSWORD here AND in /api/upload.php, /api/images.php, /api/posts.php
const ADMIN_PASSWORD   = "ptcadmin2026";
const UPLOAD_ENDPOINT  = "/api/upload.php";
const IMAGES_ENDPOINT  = "/api/images.php";
const POSTS_ENDPOINT   = "/api/posts.php";
const PAGES_ENDPOINT   = "/api/custom-pages.php";
const GA4_ID           = "G-MB1N8JQLT1";
const SITE_URL         = "https://primetenancycleaning.com";

// ── Helpers ───────────────────────────────────────────────────────────────
const toSlug = (t) =>
  t.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();

const wordCount = (html) =>
  (html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;

const seoAnalysis = ({ title, slug, metaTitle, metaDescription, focusKeyword, content, featuredImage }) => {
  const kw   = (focusKeyword || "").toLowerCase().trim();
  const body = (content || "").toLowerCase();
  const desc = (metaDescription || "");
  const mt   = (metaTitle || title || "").toLowerCase();

  const checks = [
    {
      label: "Focus keyword in SEO title",
      pass: kw && mt.includes(kw),
      tip: "Add the focus keyword to your SEO title",
    },
    {
      label: "Focus keyword in meta description",
      pass: kw && desc.toLowerCase().includes(kw),
      tip: "Include the focus keyword in the meta description",
    },
    {
      label: "Focus keyword in content",
      pass: kw && body.includes(kw),
      tip: "Use the focus keyword naturally in the post body",
    },
    {
      label: `Content length (${wordCount(content)} words — aim for 600+)`,
      pass: wordCount(content) >= 600,
      tip: "Write at least 600 words for better SEO",
    },
    {
      label: `Meta description (${desc.length} chars — aim for 120–160)`,
      pass: desc.length >= 120 && desc.length <= 160,
      tip: "Keep meta description between 120 and 160 characters",
    },
    {
      label: "H2 heading in content",
      pass: (content || "").includes("<h2"),
      tip: "Add at least one H2 heading to structure your content",
    },
    {
      label: "Featured image set",
      pass: !!featuredImage,
      tip: "Add a featured image for social sharing and post cards",
    },
    {
      label: "SEO-friendly slug",
      pass: slug.length > 0 && /^[a-z0-9-]+$/.test(slug),
      tip: "Slug should use lowercase letters, numbers and hyphens only",
    },
  ];

  const score = checks.filter((c) => c.pass).length;
  return { score, total: checks.length, checks };
};

const scoreColor = (score, total) => {
  const pct = score / total;
  if (pct >= 0.875) return "text-green-600 bg-green-100";
  if (pct >= 0.625) return "text-yellow-600 bg-yellow-100";
  if (pct >= 0.375) return "text-orange-600 bg-orange-100";
  return "text-red-600 bg-red-100";
};

const scoreLabel = (score, total) => {
  const pct = score / total;
  if (pct >= 0.875) return "Excellent";
  if (pct >= 0.625) return "Good";
  if (pct >= 0.375) return "Needs work";
  return "Poor";
};

const fmtDate = (ts) =>
  ts ? new Date(ts * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const EMPTY_POST = {
  title: "", slug: "", excerpt: "", content: "",
  featuredImage: "", metaTitle: "", metaDescription: "",
  focusKeyword: "", status: "draft",
};

const EMPTY_PAGE = {
  title: "", slug: "",
  heroEyebrow: "", heroHeading: "", heroSubheading: "", heroImage: "",
  content: "", metaTitle: "", metaDescription: "",
  focusKeyword: "", status: "draft",
};

// ── Plugins data ──────────────────────────────────────────────────────────
const PLUGINS = [
  {
    name: "Rank Math SEO",
    description:
      "SEO analysis is built into the Post Editor — meta title, meta description, focus keyword, content score and 8-point checklist are all handled there.",
    status: "active",
    tag: "Built-in",
    tagColor: "bg-green-100 text-green-700",
    link: null,
    note: "Planning to move to WordPress? Get Rank Math free at rankmath.com",
  },
  {
    name: "Google Analytics 4",
    description: `Traffic, sessions, bounce rate and conversion tracking via property ${GA4_ID}, installed in index.html.`,
    status: "active",
    tag: "Active",
    tagColor: "bg-green-100 text-green-700",
    link: "https://analytics.google.com/analytics/web/",
  },
  {
    name: "Google Search Console",
    description: "Keyword rankings, impressions, CTR and crawl coverage. Submit sitemap.xml here.",
    status: "active",
    tag: "Active",
    tagColor: "bg-green-100 text-green-700",
    link: `https://search.google.com/search-console?resource_id=${encodeURIComponent(SITE_URL)}`,
  },
  {
    name: "Jotform",
    description: "Quote request form on home and contact pages. Form ID: 261773727058062.",
    status: "active",
    tag: "Active",
    tagColor: "bg-green-100 text-green-700",
    link: "https://www.jotform.com/inbox/",
  },
  {
    name: "Zenbooker",
    description: "Live online booking widget on the /book-now page.",
    status: "active",
    tag: "Active",
    tagColor: "bg-green-100 text-green-700",
    link: "https://www.zenbooker.com/dashboard",
  },
  {
    name: "Trustpilot",
    description: "Business review platform. Account setup in progress — collect first reviews to unlock the widget.",
    status: "pending",
    tag: "Setup pending",
    tagColor: "bg-yellow-100 text-yellow-700",
    link: "https://business.trustpilot.com/",
  },
  {
    name: "Google Business Profile",
    description: "Local SEO listing on Google Maps and Search. Required for local ranking in London boroughs.",
    status: "pending",
    tag: "Setup pending",
    tagColor: "bg-yellow-100 text-yellow-700",
    link: "https://business.google.com/",
  },
];

// ── Login ─────────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [pw, setPw]       = useState("");
  const [show, setShow]   = useState(false);
  const [err, setErr]     = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) { onLogin(); }
    else { setErr("Incorrect password."); setPw(""); }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gold rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-navy" />
          </div>
          <h1 className="text-2xl font-bold text-white">PTC Admin</h1>
          <p className="text-slate-400 text-sm mt-1">primetenancycleaning.com</p>
        </div>
        <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Admin Password</label>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => { setPw(e.target.value); setErr(""); }}
                placeholder="Enter password"
                autoFocus
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-gold pr-12"
              />
              <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {err && <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />{err}</p>}
          </div>
          <button type="submit" className="w-full bg-gold hover:bg-gold-dark text-navy font-bold py-3 rounded-xl transition-colors">
            Access Dashboard
          </button>
        </form>
        <p className="text-slate-600 text-xs text-center mt-6">Restricted access — PTC internal use only</p>
      </div>
    </div>
  );
};

// ── Post Editor ───────────────────────────────────────────────────────────
const PostEditor = ({ post, onSave, onBack }) => {
  const [form, setForm]               = useState(post || EMPTY_POST);
  const [preview, setPreview]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState("");
  const [imgUploading, setImgUploading]             = useState(false);
  const [contentImgUploading, setContentImgUploading] = useState(false);
  const contentRef    = useRef(null);
  const imgRef        = useRef(null);
  const contentImgRef = useRef(null);
  const insertPosRef  = useRef(null);
  const isNew         = !post?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-generate slug from title (only on new posts)
  const handleTitle = (v) => {
    setForm(f => ({ ...f, title: v, ...(isNew ? { slug: toSlug(v) } : {}) }));
  };

  // Toolbar: wrap selected text in a tag
  const insertTag = (tag, attrs = "") => {
    const el = contentRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const val = el.value;
    const open = `<${tag}${attrs ? " " + attrs : ""}>`;
    const close = `</${tag}>`;
    const newVal = val.slice(0, s) + open + val.slice(s, e) + close + val.slice(e);
    set("content", newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + open.length, e + open.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL (include https://):");
    if (url) insertTag("a", `href="${url}"`);
  };

  const insertImage = () => {
    const el = contentRef.current;
    insertPosRef.current = el ? el.selectionStart : (form.content || "").length;
    contentImgRef.current?.click();
  };

  const save = async (status) => {
    if (!form.title.trim()) { setErr("Title is required."); return; }
    if (!form.slug.trim())  { setErr("Slug is required."); return; }
    setSaving(true); setErr("");
    const payload = { ...form, status, metaTitle: form.metaTitle || form.title };
    try {
      const res = await fetch(POSTS_ENDPOINT, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json", "X-Admin-Token": ADMIN_PASSWORD },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) { onSave(); }
      else setErr(data.error || "Save failed.");
    } catch { setErr("Could not reach the server. Is the site deployed?"); }
    finally { setSaving(false); }
  };

  const seo = seoAnalysis(form);

  const TOOLBAR_BTNS = [
    { label: "B",   title: "Bold",        action: () => insertTag("strong") },
    { label: "I",   title: "Italic",      action: () => insertTag("em") },
    { label: "H2",  title: "Heading 2",   action: () => insertTag("h2") },
    { label: "H3",  title: "Heading 3",   action: () => insertTag("h3") },
    { label: "UL",  title: "List item",   action: () => insertTag("li") },
    { label: "🔗",  title: "Link",        action: insertLink },
    { label: "🖼",  title: "Image",       action: insertImage },
  ];

  return (
    <div>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-navy text-sm font-medium">
          <ChevronLeft className="w-4 h-4" /> Back to posts
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => save("draft")} disabled={saving}
            className="border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
            Save Draft
          </button>
          <button onClick={() => save("published")} disabled={saving}
            className="bg-navy text-white hover:bg-navy-light px-5 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{err}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── Left: Content ── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleTitle(e.target.value)}
              placeholder="Post title…"
              className="w-full text-2xl font-bold text-navy placeholder-slate-300 focus:outline-none"
            />
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-slate-400">Slug:</span>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => set("slug", toSlug(e.target.value))}
                className="flex-1 text-xs font-mono text-slate-600 focus:outline-none border-b border-slate-200 pb-0.5"
              />
              <button onClick={() => navigator.clipboard.writeText(`${SITE_URL}/blog/${form.slug}`)}
                className="text-slate-400 hover:text-navy"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {/* Excerpt */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Excerpt</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              placeholder="Short summary shown on the blog listing page…"
              rows={2}
              className="w-full text-sm text-slate-700 placeholder-slate-300 focus:outline-none resize-none"
            />
          </div>

          {/* Content */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-4 py-2.5 border-b border-slate-100 flex-wrap">
              <input
                ref={contentImgRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { alert("Max 5 MB"); return; }
                  setContentImgUploading(true);
                  const fd = new FormData();
                  fd.append("image", file);
                  try {
                    const res = await fetch(UPLOAD_ENDPOINT, {
                      method: "POST",
                      headers: { "X-Admin-Token": ADMIN_PASSWORD },
                      body: fd,
                    });
                    const data = await res.json();
                    if (data.success) {
                      const pos = insertPosRef.current ?? 0;
                      const tag = `<img src="${data.url}" alt="" class="w-full rounded-xl my-4" />`;
                      setForm(f => {
                        const c = f.content || "";
                        return { ...f, content: c.slice(0, pos) + tag + c.slice(pos) };
                      });
                    } else { alert(data.error || "Upload failed"); }
                  } catch { alert("Upload failed — is the site deployed?"); }
                  finally { setContentImgUploading(false); e.target.value = ""; }
                }}
              />
              {TOOLBAR_BTNS.map((btn) => (
                <button
                  key={btn.label}
                  onClick={btn.action}
                  title={btn.title}
                  disabled={btn.title === "Image" && contentImgUploading}
                  className="text-xs font-bold text-slate-600 hover:bg-slate-100 hover:text-navy px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {btn.title === "Image" && contentImgUploading ? "…" : btn.label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-slate-400">{wordCount(form.content)} words</span>
                <button
                  onClick={() => setPreview(v => !v)}
                  className="text-xs font-semibold text-teal border border-teal/30 px-3 py-1.5 rounded-lg hover:bg-teal/5"
                >
                  {preview ? "Edit" : "Preview"}
                </button>
              </div>
            </div>

            {preview ? (
              <div
                className="p-5 prose prose-sm max-w-none min-h-[300px] text-slate-700"
                dangerouslySetInnerHTML={{ __html: form.content || "<p class='text-slate-400'>Nothing to preview yet.</p>" }}
              />
            ) : (
              <textarea
                ref={contentRef}
                value={form.content}
                onChange={(e) => set("content", e.target.value)}
                placeholder={`Write your post content here using HTML tags.\n\nExample:\n<h2>Why Choose Professional End of Tenancy Cleaning?</h2>\n<p>Moving out of a rental property in London...</p>`}
                rows={20}
                className="w-full p-5 text-sm text-slate-700 placeholder-slate-300 focus:outline-none resize-y font-mono"
              />
            )}
          </div>
        </div>

        {/* ── Right: Sidebar ── */}
        <div className="space-y-4">
          {/* Publish */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-navy text-sm mb-4">Publish</h3>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-600">Status</span>
              <button onClick={() => set("status", form.status === "published" ? "draft" : "published")}
                className="flex items-center gap-2 text-sm font-semibold">
                {form.status === "published"
                  ? <><ToggleRight className="w-5 h-5 text-green-600" /><span className="text-green-600">Published</span></>
                  : <><ToggleLeft className="w-5 h-5 text-slate-400" /><span className="text-slate-500">Draft</span></>}
              </button>
            </div>
            {form.slug && (
              <a
                href={`/blog/${form.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-teal hover:text-navy flex items-center gap-1 truncate"
              >
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                /blog/{form.slug}
              </a>
            )}
          </div>

          {/* Featured Image */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-navy text-sm mb-3">Featured Image</h3>
            <input ref={imgRef} type="file" accept="image/*" className="hidden"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) { alert("Max file size is 5 MB"); return; }
                setImgUploading(true);
                const fd = new FormData();
                fd.append("image", file);
                try {
                  const res = await fetch(UPLOAD_ENDPOINT, {
                    method: "POST",
                    headers: { "X-Admin-Token": ADMIN_PASSWORD },
                    body: fd,
                  });
                  const data = await res.json();
                  if (data.success) set("featuredImage", data.url);
                  else alert(data.error || "Upload failed");
                } catch { alert("Upload failed — is the site deployed?"); }
                finally { setImgUploading(false); e.target.value = ""; }
              }}
            />
            <button
              type="button"
              onClick={() => imgRef.current?.click()}
              disabled={imgUploading}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-4 hover:border-teal hover:bg-slate-50 transition-all text-slate-500 hover:text-teal disabled:opacity-50 mb-2"
            >
              {imgUploading
                ? <><RefreshCw className="w-4 h-4 animate-spin" /><span className="text-xs font-medium">Uploading…</span></>
                : <><Upload className="w-4 h-4" /><span className="text-xs font-medium">Upload image</span></>}
            </button>
            <input
              type="text"
              value={form.featuredImage}
              onChange={(e) => set("featuredImage", e.target.value)}
              placeholder="or paste URL: /images/your-image.jpg"
              className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal font-mono mb-2"
            />
            {form.featuredImage && (
              <div className="relative aspect-video bg-slate-100 rounded-xl overflow-hidden">
                <img src={form.featuredImage} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => set("featuredImage", "")}
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          {/* SEO — Rank Math style */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-navy text-sm">SEO Settings</h3>
              <div className={`text-xs font-bold px-2.5 py-1 rounded-full ${scoreColor(seo.score, seo.total)}`}>
                {seo.score}/{seo.total} · {scoreLabel(seo.score, seo.total)}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Focus Keyword</label>
                <input
                  type="text"
                  value={form.focusKeyword}
                  onChange={(e) => set("focusKeyword", e.target.value)}
                  placeholder="e.g. end of tenancy cleaning Hackney"
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">SEO Title</label>
                <input
                  type="text"
                  value={form.metaTitle}
                  onChange={(e) => set("metaTitle", e.target.value)}
                  placeholder={form.title || "Auto-filled from title"}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Meta Description{" "}
                  <span className={
                    (form.metaDescription.length >= 120 && form.metaDescription.length <= 160)
                      ? "text-green-600" : "text-orange-500"
                  }>
                    ({form.metaDescription.length}/160)
                  </span>
                </label>
                <textarea
                  value={form.metaDescription}
                  onChange={(e) => set("metaDescription", e.target.value)}
                  placeholder="Write a compelling description (120–160 chars)…"
                  rows={3}
                  maxLength={160}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal resize-none"
                />
              </div>
            </div>

            {/* SEO checklist */}
            <div className="mt-4 space-y-2">
              {seo.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  {c.pass
                    ? <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    : <AlertCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0 mt-0.5" />}
                  <span className={`text-xs leading-snug ${c.pass ? "text-slate-500" : "text-slate-600"}`}>
                    {c.pass ? c.label : c.tip}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Posts Tab ─────────────────────────────────────────────────────────────
const PostsTab = () => {
  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editingPost, setEditing] = useState(null); // null = list, object = editing
  const [isNew, setIsNew]         = useState(false);
  const [deleting, setDeleting]   = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(POSTS_ENDPOINT, {
        headers: { "X-Admin-Token": ADMIN_PASSWORD },
      });
      const data = await res.json();
      setPosts(data.posts || []);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await fetch(`${POSTS_ENDPOINT}?id=${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": ADMIN_PASSWORD },
      });
      fetchPosts();
    } finally { setDeleting(null); }
  };

  const openNew = () => { setEditing({ ...EMPTY_POST }); setIsNew(true); };
  const openEdit = (p) => { setEditing(p); setIsNew(false); };
  const backToList = () => { setEditing(null); fetchPosts(); };

  if (editingPost !== null) {
    return <PostEditor post={isNew ? null : editingPost} onSave={backToList} onBack={backToList} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-navy">Blog Posts</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Posts appear at <span className="font-mono text-xs">/blog</span> on the live site
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-navy text-white hover:bg-navy-light px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
        >
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-semibold text-slate-600 mb-1">No posts yet</p>
          <p className="text-slate-400 text-sm mb-4">
            Posts only save to the live server — they won't work in local development.
          </p>
          <button
            onClick={openNew}
            className="bg-navy text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-navy-light"
          >
            Create your first post
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-6 py-3">Title</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 hidden sm:table-cell">Keyword</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 hidden md:table-cell">Words</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 hidden lg:table-cell">Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {posts.map((p) => {
                const seo = seoAnalysis(p);
                return (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-navy text-sm truncate max-w-[200px]">{p.title}</p>
                      <p className="text-xs text-slate-400 font-mono truncate max-w-[200px]">/blog/{p.slug}</p>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreColor(seo.score, seo.total)}`}>
                        SEO {seo.score}/{seo.total}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <span className="text-xs text-slate-500">{p.focusKeyword || "—"}</span>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className="text-xs text-slate-500">{wordCount(p.content)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        p.status === "published"
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {p.status === "published" ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-xs text-slate-400">{fmtDate(p.createdAt)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => openEdit(p)} title="Edit"
                          className="p-1.5 text-slate-400 hover:text-navy hover:bg-slate-100 rounded-lg transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" title="View post"
                          className="p-1.5 text-slate-400 hover:text-teal hover:bg-slate-100 rounded-lg transition-colors">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button onClick={() => deletePost(p.id)} disabled={deleting === p.id} title="Delete"
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Page Editor ───────────────────────────────────────────────────────────
const PageEditor = ({ page, onSave, onBack }) => {
  const [form, setForm]                         = useState(page || EMPTY_PAGE);
  const [preview, setPreview]                   = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [err, setErr]                           = useState("");
  const [heroImgUploading, setHeroImgUploading] = useState(false);
  const [contentImgUploading, setContentImgUploading] = useState(false);
  const contentRef    = useRef(null);
  const heroImgRef    = useRef(null);
  const contentImgRef = useRef(null);
  const insertPosRef  = useRef(null);
  const isNew         = !page?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleTitle = (v) =>
    setForm(f => ({ ...f, title: v, ...(isNew ? { slug: toSlug(v) } : {}) }));

  const insertTag = (tag, attrs = "") => {
    const el = contentRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const val = el.value;
    const open = `<${tag}${attrs ? " " + attrs : ""}>`;
    const close = `</${tag}>`;
    const newVal = val.slice(0, s) + open + val.slice(s, e) + close + val.slice(e);
    set("content", newVal);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + open.length, e + open.length); });
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL (include https://):");
    if (url) insertTag("a", `href="${url}"`);
  };

  const insertImage = () => {
    const el = contentRef.current;
    insertPosRef.current = el ? el.selectionStart : (form.content || "").length;
    contentImgRef.current?.click();
  };

  const save = async (status) => {
    if (!form.title.trim()) { setErr("Title is required."); return; }
    if (!form.slug.trim())  { setErr("Slug is required."); return; }
    setSaving(true); setErr("");
    const payload = { ...form, status, metaTitle: form.metaTitle || form.title };
    try {
      const res = await fetch(PAGES_ENDPOINT, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json", "X-Admin-Token": ADMIN_PASSWORD },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) onSave();
      else setErr(data.error || "Save failed.");
    } catch { setErr("Could not reach the server. Is the site deployed?"); }
    finally { setSaving(false); }
  };

  const seo = seoAnalysis({ ...form, featuredImage: form.heroImage });

  const TOOLBAR = [
    { label: "B",  title: "Bold",      action: () => insertTag("strong") },
    { label: "I",  title: "Italic",    action: () => insertTag("em") },
    { label: "H2", title: "Heading 2", action: () => insertTag("h2") },
    { label: "H3", title: "Heading 3", action: () => insertTag("h3") },
    { label: "UL", title: "List item", action: () => insertTag("li") },
    { label: "🔗", title: "Link",      action: insertLink },
    { label: "🖼", title: "Image",     action: insertImage },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-navy text-sm font-medium">
          <ChevronLeft className="w-4 h-4" /> Back to pages
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => save("draft")} disabled={saving}
            className="border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
            Save Draft
          </button>
          <button onClick={() => save("published")} disabled={saving}
            className="bg-navy text-white hover:bg-navy-light px-5 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{err}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title + slug */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <input
              type="text" value={form.title} onChange={(e) => handleTitle(e.target.value)}
              placeholder="Page title…"
              className="w-full text-2xl font-bold text-navy placeholder-slate-300 focus:outline-none"
            />
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-slate-400">Slug:</span>
              <input type="text" value={form.slug}
                onChange={(e) => set("slug", toSlug(e.target.value))}
                className="flex-1 text-xs font-mono text-slate-600 focus:outline-none border-b border-slate-200 pb-0.5"
              />
              <button onClick={() => navigator.clipboard.writeText(`${SITE_URL}/p/${form.slug}`)}
                className="text-slate-400 hover:text-navy"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {/* Hero section */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Hero Section</h3>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Eyebrow label (optional)</label>
              <input type="text" value={form.heroEyebrow}
                onChange={(e) => set("heroEyebrow", e.target.value)}
                placeholder="e.g. Our Services"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Hero heading (defaults to page title)</label>
              <input type="text" value={form.heroHeading}
                onChange={(e) => set("heroHeading", e.target.value)}
                placeholder={form.title || "Page title used if blank"}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Subheading (optional)</label>
              <textarea value={form.heroSubheading}
                onChange={(e) => set("heroSubheading", e.target.value)}
                placeholder="A short supporting sentence shown below the heading…"
                rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal resize-none"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Hero background image</label>
              <input ref={heroImgRef} type="file" accept="image/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { alert("Max 5 MB"); return; }
                  setHeroImgUploading(true);
                  const fd = new FormData();
                  fd.append("image", file);
                  try {
                    const res = await fetch(UPLOAD_ENDPOINT, { method: "POST", headers: { "X-Admin-Token": ADMIN_PASSWORD }, body: fd });
                    const data = await res.json();
                    if (data.success) set("heroImage", data.url);
                    else alert(data.error || "Upload failed");
                  } catch { alert("Upload failed — is the site deployed?"); }
                  finally { setHeroImgUploading(false); e.target.value = ""; }
                }}
              />
              <button type="button" onClick={() => heroImgRef.current?.click()} disabled={heroImgUploading}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-3 hover:border-teal hover:bg-slate-50 transition-all text-slate-500 hover:text-teal disabled:opacity-50 mb-2">
                {heroImgUploading
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /><span className="text-xs font-medium">Uploading…</span></>
                  : <><Upload className="w-4 h-4" /><span className="text-xs font-medium">Upload hero image</span></>}
              </button>
              <input type="text" value={form.heroImage}
                onChange={(e) => set("heroImage", e.target.value)}
                placeholder="or paste URL: /images/your-photo.jpg"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal font-mono"
              />
              {form.heroImage && (
                <div className="relative mt-2 aspect-video bg-slate-100 rounded-xl overflow-hidden">
                  <img src={form.heroImage} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("heroImage", "")}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content editor */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="flex items-center gap-1 px-4 py-2.5 border-b border-slate-100 flex-wrap">
              <input ref={contentImgRef} type="file" accept="image/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { alert("Max 5 MB"); return; }
                  setContentImgUploading(true);
                  const fd = new FormData();
                  fd.append("image", file);
                  try {
                    const res = await fetch(UPLOAD_ENDPOINT, { method: "POST", headers: { "X-Admin-Token": ADMIN_PASSWORD }, body: fd });
                    const data = await res.json();
                    if (data.success) {
                      const pos = insertPosRef.current ?? 0;
                      const tag = `<img src="${data.url}" alt="" class="w-full rounded-xl my-4" />`;
                      setForm(f => { const c = f.content || ""; return { ...f, content: c.slice(0, pos) + tag + c.slice(pos) }; });
                    } else { alert(data.error || "Upload failed"); }
                  } catch { alert("Upload failed — is the site deployed?"); }
                  finally { setContentImgUploading(false); e.target.value = ""; }
                }}
              />
              {TOOLBAR.map((btn) => (
                <button key={btn.label} onClick={btn.action} title={btn.title}
                  disabled={btn.title === "Image" && contentImgUploading}
                  className="text-xs font-bold text-slate-600 hover:bg-slate-100 hover:text-navy px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                  {btn.title === "Image" && contentImgUploading ? "…" : btn.label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-slate-400">{wordCount(form.content)} words</span>
                <button onClick={() => setPreview(v => !v)}
                  className="text-xs font-semibold text-teal border border-teal/30 px-3 py-1.5 rounded-lg hover:bg-teal/5">
                  {preview ? "Edit" : "Preview"}
                </button>
              </div>
            </div>
            {preview ? (
              <div className="p-5 prose prose-sm max-w-none min-h-[300px] text-slate-700"
                dangerouslySetInnerHTML={{ __html: form.content || "<p class='text-slate-400'>Nothing to preview yet.</p>" }} />
            ) : (
              <textarea ref={contentRef} value={form.content}
                onChange={(e) => set("content", e.target.value)}
                placeholder={"Write your page content here using HTML tags.\n\nExample:\n<h2>Section Heading</h2>\n<p>Your content here...</p>"}
                rows={20}
                className="w-full p-5 text-sm text-slate-700 placeholder-slate-300 focus:outline-none resize-y font-mono"
              />
            )}
          </div>
        </div>

        {/* Right: sidebar */}
        <div className="space-y-4">
          {/* Publish */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-navy text-sm mb-4">Publish</h3>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-600">Status</span>
              <button onClick={() => set("status", form.status === "published" ? "draft" : "published")}
                className="flex items-center gap-2 text-sm font-semibold">
                {form.status === "published"
                  ? <><ToggleRight className="w-5 h-5 text-green-600" /><span className="text-green-600">Published</span></>
                  : <><ToggleLeft className="w-5 h-5 text-slate-400" /><span className="text-slate-500">Draft</span></>}
              </button>
            </div>
            {form.slug && (
              <a href={`/p/${form.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-teal hover:text-navy flex items-center gap-1 truncate">
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                /p/{form.slug}
              </a>
            )}
          </div>

          {/* SEO */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-navy text-sm">SEO Settings</h3>
              <div className={`text-xs font-bold px-2.5 py-1 rounded-full ${scoreColor(seo.score, seo.total)}`}>
                {seo.score}/{seo.total} · {scoreLabel(seo.score, seo.total)}
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Focus Keyword</label>
                <input type="text" value={form.focusKeyword}
                  onChange={(e) => set("focusKeyword", e.target.value)}
                  placeholder="e.g. end of tenancy cleaning London"
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">SEO Title</label>
                <input type="text" value={form.metaTitle}
                  onChange={(e) => set("metaTitle", e.target.value)}
                  placeholder={form.title || "Auto-filled from title"}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Meta Description{" "}
                  <span className={(form.metaDescription.length >= 120 && form.metaDescription.length <= 160) ? "text-green-600" : "text-orange-500"}>
                    ({form.metaDescription.length}/160)
                  </span>
                </label>
                <textarea value={form.metaDescription}
                  onChange={(e) => set("metaDescription", e.target.value)}
                  placeholder="120–160 chars…" rows={3} maxLength={160}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal resize-none"
                />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {seo.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  {c.pass
                    ? <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    : <AlertCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0 mt-0.5" />}
                  <span className={`text-xs leading-snug ${c.pass ? "text-slate-500" : "text-slate-600"}`}>
                    {c.pass ? c.label : c.tip}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Pages Tab ─────────────────────────────────────────────────────────────
const PagesTab = () => {
  const [pages, setPages]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editingPage, setEditing] = useState(null);
  const [isNew, setIsNew]         = useState(false);
  const [deleting, setDeleting]   = useState(null);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(PAGES_ENDPOINT, { headers: { "X-Admin-Token": ADMIN_PASSWORD } });
      const data = await res.json();
      setPages(data.pages || []);
    } catch { setPages([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const deletePage = async (id) => {
    if (!window.confirm("Delete this page? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await fetch(`${PAGES_ENDPOINT}?id=${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": ADMIN_PASSWORD },
      });
      fetchPages();
    } finally { setDeleting(null); }
  };

  const openNew  = () => { setEditing({ ...EMPTY_PAGE }); setIsNew(true); };
  const openEdit = (p) => { setEditing(p); setIsNew(false); };
  const backToList = () => { setEditing(null); fetchPages(); };

  if (editingPage !== null) {
    return <PageEditor page={isNew ? null : editingPage} onSave={backToList} onBack={backToList} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-navy">Pages</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Custom pages live at <span className="font-mono text-xs">/p/slug</span> on the site
          </p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-2 bg-navy text-white hover:bg-navy-light px-4 py-2.5 rounded-xl text-sm font-bold transition-colors">
          <Plus className="w-4 h-4" /> Add Page
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Loading pages…</div>
      ) : pages.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl">
          <Layout className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-semibold text-slate-600 mb-1">No pages yet</p>
          <p className="text-slate-400 text-sm mb-4">Create a page and it will be live at /p/slug instantly.</p>
          <button onClick={openNew}
            className="bg-navy text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-navy-light">
            Create your first page
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-6 py-3">Title</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 hidden sm:table-cell">URL</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 hidden lg:table-cell">Updated</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {pages.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-semibold text-navy text-sm truncate max-w-[200px]">{p.title}</p>
                    {p.heroSubheading && (
                      <p className="text-xs text-slate-400 truncate max-w-[200px] mt-0.5">{p.heroSubheading}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 hidden sm:table-cell">
                    <span className="text-xs font-mono text-slate-500">/p/{p.slug}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      p.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {p.status === "published" ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <span className="text-xs text-slate-400">{fmtDate(p.updatedAt)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => openEdit(p)} title="Edit"
                        className="p-1.5 text-slate-400 hover:text-navy hover:bg-slate-100 rounded-lg transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" title="View page"
                        className="p-1.5 text-slate-400 hover:text-teal hover:bg-slate-100 rounded-lg transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button onClick={() => deletePage(p.id)} disabled={deleting === p.id} title="Delete"
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Site Content Tab ──────────────────────────────────────────────────────
const SITE_CONTENT_ENDPOINT = "/api/site-content.php";

// helper ─ inline field renderer
const FieldInput = ({ field: f, value, onChange }) => {
  if (f.type === "textarea")
    return <textarea value={value ?? f.default} onChange={e => onChange(e.target.value)}
      rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal resize-y" />;
  if (f.type === "number")
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
        <input type="number" value={value ?? f.default} onChange={e => onChange(e.target.value)}
          className="w-full text-sm border border-slate-200 rounded-lg pl-7 pr-3 py-2 focus:outline-none focus:border-teal" />
      </div>
    );
  return <input type="text" value={value ?? f.default} onChange={e => onChange(e.target.value)}
    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal" />;
};

const T = (name, label, type, def) => ({ name, label, type, default: def });

const SITE_PAGES = [
  {
    key: "home", label: "Homepage", path: "/",
    sections: [
      { label: "Hero", fields: [
        T("badge",    "Badge text",      "text",     "London's Trusted End of Tenancy Cleaning"),
        T("h1Before", "H1 before gold",  "text",     "Move Out"),
        T("h1Gold",   "H1 gold word",    "text",     "Stress-Free"),
        T("h1After",  "H1 after gold",   "text",     "in London"),
        T("subtitle", "Hero subtitle",   "textarea", "Professional end of tenancy cleaning across all 32 London boroughs. Inventory-ready finishes, estate-agent standards and a 7-day re-clean guarantee."),
      ]},
      { label: "Services Section", fields: [
        T("services_eyebrow",  "Eyebrow",    "text",     "Our Services"),
        T("services_heading",  "Heading",    "text",     "Specialist cleaning for every London tenancy"),
        T("services_sub",      "Subheading", "textarea", "Pick a single service or bundle them together for a bigger discount and one team that handles your entire move-out."),
      ]},
      { label: "About / Intro Copy", fields: [
        T("intro_eyebrow",  "Eyebrow",    "text",     "About Prime Tenancy"),
        T("intro_heading",  "Heading",    "text",     "London move-out cleaning, built around the way letting agents actually inspect."),
        T("intro_p1",       "Paragraph 1","textarea", "Prime Tenancy Cleaning is a London-based move-out cleaning company serving tenants, landlords and letting agents across all 32 boroughs. Every job we run is benchmarked against the independent inventory standard used by major London agencies, so what arrives at your property is not a generic domestic clean — it is a checkout-ready specification designed to clear an inventory check on the first visit."),
        T("intro_p2",       "Paragraph 2","textarea", "We focus exclusively on the highest-scrutiny rentals in zones 1 to 6: studios in Zone 1 mansion blocks, converted Victorian flats, modern build-to-rent towers and family homes in the outer London boroughs. Our cleaners arrive equipped with professional-grade tools, dip-tanks for oven cleaning in London, hot-water carpet extractors for end of tenancy carpet cleaning and eco-certified products that are safe around children, pets and sensitive surfaces."),
        T("intro_p3",       "Paragraph 3","textarea", "Whether you are a tenant aiming for a smooth checkout, a landlord preparing a flat for re-letting, or an agent managing a portfolio of London properties, our team gives you one dependable contractor with a written, 7-day re-clean guarantee behind every job."),
      ]},
      { label: "Pricing Section", fields: [
        T("pricing_eyebrow",  "Eyebrow",    "text",     "Pricing"),
        T("pricing_heading",  "Heading",    "text",     "Transparent Pricing"),
        T("pricing_sub",      "Subheading", "textarea", "No hidden fees. Get an instant estimate based on your property size — see the full breakdown on our prices page."),
      ]},
      { label: "FAQ Section", fields: [
        T("home_faq_heading", "Section heading",    "text",     "Frequently Asked Questions"),
        T("home_faq_sub",     "Section subheading", "textarea", "Quick answers to the questions tenants and landlords ask us most about end of tenancy cleaning in London."),
        T("home_faq1_q", "FAQ 1 question", "text",     "What does end of tenancy cleaning in London include?"),
        T("home_faq1_a", "FAQ 1 answer",   "textarea", "Our move-out cleaning in London follows the standard inventory checklist used by London letting agents — every room is deep cleaned including kitchen appliances inside and out, bathroom limescale removal, internal windows, skirting boards, light fittings, switches, sockets and door tops. We focus on the high-scrutiny areas inspectors check first."),
        T("home_faq2_q", "FAQ 2 question", "text",     "Are your tenancy cleaners insured and DBS-vetted?"),
        T("home_faq2_a", "FAQ 2 answer",   "textarea", "Yes. Every cleaner working on a Prime Tenancy job is fully employed, trained on the AIIC inventory standard, and covered by £5M public liability insurance. We do not use day-labour or unvetted subcontractors."),
        T("home_faq3_q", "FAQ 3 question", "text",     "Do you offer a re-clean guarantee?"),
        T("home_faq3_a", "FAQ 3 answer",   "textarea", "We do. If your landlord or letting agent flags any cleaning issue within 7 days of our visit, we return and re-clean the flagged areas at no extra cost. It is the strongest written guarantee available from any tenancy cleaner in London."),
        T("home_faq4_q", "FAQ 4 question", "text",     "How much does end of tenancy cleaning cost in London?"),
        T("home_faq4_a", "FAQ 4 answer",   "textarea", "Studio flats start from £99, one-bedroom from £149, two-bedroom from £179 and three-bedroom homes from £230. Add-ons such as carpet steam cleaning, oven dip-tank degreasing and exterior windows are priced separately and quoted up front — no hidden fees."),
        T("home_faq5_q", "FAQ 5 question", "text",     "Can you clean on weekends or at short notice?"),
        T("home_faq5_a", "FAQ 5 answer",   "textarea", "Yes. We operate seven days a week including Saturdays, Sundays and most public holidays, and we routinely fit in same-day and next-day jobs at the end of each month when most London tenancies end."),
      ]},
      { label: "Quote Section", fields: [
        T("quote_eyebrow",  "Eyebrow",    "text",     "Get Started"),
        T("quote_heading",  "Heading",    "text",     "Ready for a Stress-Free Move-Out?"),
        T("quote_sub",      "Subheading", "textarea", "Fill out our quick form below and receive a tailored end of tenancy cleaning quote within 24 hours."),
      ]},
    ],
  },
  {
    key: "eot", label: "End of Tenancy Cleaning", path: "/end-of-tenancy-cleaning-london",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "End of Tenancy Cleaning London"),
        T("h1",         "H1 title",        "text",     "Professional End of Tenancy Cleaning"),
        T("highlight",  "H1 gold word",    "text",     "in London"),
        T("subheading", "Hero subheading", "textarea", "Inventory-check ready, landlord approved and backed by a 7-day re-clean guarantee. The same checklist your letting agent inspects against — handled in a single visit."),
      ]},
      { label: "Overview Section", fields: [
        T("overview_eyebrow",  "Eyebrow",    "text",     "Service Overview"),
        T("overview_heading",  "Heading",    "text",     "The London move-out clean built around the inventory checklist"),
        T("overview_p1", "Paragraph 1", "textarea", "A standard domestic clean and a proper end of tenancy clean are not the same job. Letting agents in London inspect against a specific inventory checklist — the same checklist used by AIIC-trained inventory clerks — and a single missed area on that checklist is often enough to trigger a deposit deduction. Our entire move-out service is built backwards from that document, so the finish your landlord sees on the checkout report is the finish we plan and price for from day one."),
        T("overview_p2", "Paragraph 2", "textarea", "We operate across every London postcode from EC and WC in Zone 1 out to TW, KT, SM, CR, BR, DA, RM, IG, EN, HA, UB and the rest of the M25. The team is fully employed, DBS-vetted and trained on the inventory benchmark, so the cleaner who turns up to a luxury serviced apartment in Mayfair works to exactly the same specification as the team handing back a HMO in Walthamstow."),
        T("overview_p3", "Paragraph 3", "textarea", "Tenants book us to chase a smooth checkout. Landlords use us between tenancies to keep their portfolio inventory-ready. Letting agents put us on their preferred-supplier list because the work clears the checkout report on the first visit."),
      ]},
      { label: "Checklist Section", fields: [
        T("checklist_eyebrow",  "Eyebrow",    "text",     "What's Included"),
        T("checklist_heading",  "Heading",    "text",     "The complete end of tenancy cleaning checklist"),
        T("checklist_sub",      "Subheading", "textarea", "Every property gets the same inventory-grade specification. Add-ons such as carpet steam cleaning, exterior windows and balcony cleans are priced separately so you only pay for what your tenancy needs."),
      ]},
      { label: "Why Choose Us", fields: [
        T("why_eyebrow",  "Eyebrow",    "text",     "Why Choose Prime Tenancy"),
        T("why_heading",  "Heading",    "text",     "The London end of tenancy cleaner landlords trust"),
        T("why_sub",      "Subheading", "textarea", "We're not the cheapest tenancy cleaner in London — we are the one most likely to clear your checkout report on the first attempt, backed by our 7-day re-clean guarantee."),
        T("why1_title","Card 1 title","text",     "Fully insured cleaners"),
        T("why1_desc", "Card 1 desc", "textarea", "Every team member carries £5M public liability insurance — protecting you, your landlord and the property during the clean."),
        T("why2_title","Card 2 title","text",     "Re-clean focus"),
        T("why2_desc", "Card 2 desc", "textarea", "We work backwards from your checkout report. Every cleaner is trained to clear the same checklist a London inventory clerk will inspect against — backed by our 7-day re-clean guarantee."),
        T("why3_title","Card 3 title","text",     "Estate-agent standards"),
        T("why3_desc", "Card 3 desc", "textarea", "Our specification mirrors the AIIC inventory benchmark used by major London agencies, so the finish reads correctly on the checkout report."),
        T("why4_title","Card 4 title","text",     "Eco-friendly products"),
        T("why4_desc", "Card 4 desc", "textarea", "Plant-based, fragrance-free degreasers and descalers — safe around children, pets and sensitive landlords without sacrificing cleaning power."),
        T("why5_title","Card 5 title","text",     "Same-day availability"),
        T("why5_desc", "Card 5 desc", "textarea", "Last-minute checkout? We routinely fit in same-day and next-day jobs at month-end when most London tenancies expire."),
        T("why6_title","Card 6 title","text",     "Weekend bookings"),
        T("why6_desc", "Card 6 desc", "textarea", "Saturday, Sunday and public holiday slots are standard — perfect for tenants juggling work, removals and a checkout in the same week."),
      ]},
      { label: "Process Steps", fields: [
        T("process_eyebrow",  "Eyebrow",    "text",     "The Process"),
        T("process_heading",  "Heading",    "text",     "From quote to clean in four steps"),
        T("process_sub",      "Subheading", "textarea", "Most London tenants book us between five and ten days before their checkout — but if you need us tomorrow, we can usually make it work."),
        T("step1_title","Step 1 title","text",     "Free online quote"),
        T("step1_desc", "Step 1 desc", "textarea", "Send us the property size, postcode and your move-out date. You receive a fixed, all-in quote with no hidden add-ons within a few working hours."),
        T("step2_title","Step 2 title","text",     "We schedule your slot"),
        T("step2_desc", "Step 2 desc", "textarea", "Choose a date that aligns with your checkout. We coordinate with your letting agent or inventory clerk so the timing works for everyone."),
        T("step3_title","Step 3 title","text",     "Inventory-grade clean"),
        T("step3_desc", "Step 3 desc", "textarea", "A trained team of two to four cleaners arrives with all equipment, dip-tanks for the oven and carpet extractors if booked."),
        T("step4_title","Step 4 title","text",     "Walkthrough & guarantee"),
        T("step4_desc", "Step 4 desc", "textarea", "We walk through the property with you (or send photos if you have already left) and back the work with a 7-day re-clean guarantee."),
      ]},
      { label: "Inventory Specifics", fields: [
        T("spec_eyebrow",  "Eyebrow",          "text",     "Inventory Check Cleaning"),
        T("spec_heading",  "Heading",           "text",     "Built for the way London inventory clerks inspect"),
        T("spec_p1",       "Paragraph 1",       "textarea", "An inventory clerk does not start with the floor. They start with the tops of doors, the back of the oven and the limescale around the shower seal — the exact spots tenants forget. We train every cleaner to start the visit at the same points the clerk will, which is why our checkout pass rate is significantly higher than a generic domestic team's."),
        T("spec_p2",       "Paragraph 2",       "textarea", "For tenants on a strict checkout-day timeline, we can also coordinate directly with your letting agent's inventory clerk so they walk into a property ready for sign-off rather than a re-clean callback."),
        T("spec_sidebar",  "Sidebar heading",   "text",     "Common deposit deduction triggers we fix"),
      ]},
      { label: "FAQ Section", fields: [
        T("faq_heading", "Section heading",    "text",     "End of tenancy cleaning London FAQs"),
        T("faq_sub",     "Section subheading", "textarea", "The most common questions tenants and landlords ask about move-out cleans in London."),
        T("faq1_q","FAQ 1 question","text",     "What is included in your end of tenancy cleaning service in London?"),
        T("faq1_a","FAQ 1 answer",  "textarea", "Our end of tenancy cleaning in London is a full inventory-grade move-out clean. We deep clean the kitchen including inside cupboards and the oven, descale the bathroom, wipe every surface, hand-clean skirting boards, light switches, door tops and frames, and finish with floors and internal windows. The job is built around the 44-point checklist letting agents use during the checkout report."),
        T("faq2_q","FAQ 2 question","text",     "Do professional end of tenancy cleaners guarantee my deposit will be returned?"),
        T("faq2_a","FAQ 2 answer",  "textarea", "No cleaning company can sign a deposit decision in your landlord's name, but a credible end of tenancy cleaner can guarantee the cleaning standard. We give you a written 7-day re-clean guarantee: if your landlord or inventory clerk raises any cleaning issue on the checkout report, our team returns and re-cleans the flagged areas at no additional cost."),
        T("faq3_q","FAQ 3 question","text",     "How long does an end of tenancy clean take in a London flat?"),
        T("faq3_a","FAQ 3 answer",  "textarea", "A studio takes around 3 to 4 hours, a one-bedroom flat 4 to 6 hours, and a three-bedroom house 7 to 9 hours. Larger or heavily soiled properties may need a second day or an extra cleaner. We always confirm the time estimate up front so you can hand the keys back the same day."),
        T("faq4_q","FAQ 4 question","text",     "Do I need to be there during the end of tenancy clean?"),
        T("faq4_a","FAQ 4 answer",  "textarea", "No. Most London tenants meet us on arrival, hand over the keys and return at the end. We are fully insured, DBS-vetted and used to working with managing agents, building concierge desks and lock-boxes. We can also coordinate directly with your letting agent if that is simpler."),
        T("faq5_q","FAQ 5 question","text",     "Is end of tenancy cleaning landlord approved across London letting agents?"),
        T("faq5_a","FAQ 5 answer",  "textarea", "Yes. Our cleaning checklist mirrors the AIIC inventory standard that the main London letting agencies — Foxtons, Hamptons, Knight Frank, Dexters, Chestertons and most independents — rely on for their checkout reports. We routinely work alongside their inventory clerks."),
        T("faq6_q","FAQ 6 question","text",     "Do you provide an inventory check ready clean for landlords?"),
        T("faq6_a","FAQ 6 answer",  "textarea", "Absolutely. Landlords use us between tenancies to prepare a property for the next inventory check-in. The same checklist that satisfies a checkout report works perfectly for a fresh check-in, and we can schedule cleaning to fit your inventory clerk's slot."),
      ]},
      { label: "CTA", fields: [
        T("cta_title", "CTA title",       "text",     "Book your London end of tenancy clean"),
        T("cta_desc",  "CTA description", "textarea", "Get a fixed-price quote with no hidden fees, or call our London team and we'll talk through your checkout in minutes."),
      ]},
    ],
  },
  {
    key: "deep", label: "Deep Cleaning", path: "/deep-cleaning-london",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "Deep Cleaning London"),
        T("h1",         "H1 title",        "text",     "One-Off Deep Cleaning"),
        T("highlight",  "H1 gold word",    "text",     "for London Homes"),
        T("subheading", "Hero subheading", "textarea", "A top-to-bottom reset for the surfaces a weekly clean never touches — inside cupboards, behind appliances, tile grout, tops of doors and ceiling vents."),
      ]},
      { label: "Overview Section", fields: [
        T("overview_eyebrow",  "Eyebrow",    "text",     "Service Overview"),
        T("overview_heading",  "Heading",    "text",     "The professional deep clean Londoners book when \"tidy\" isn't enough"),
        T("overview_p1", "Paragraph 1", "textarea", "London homes accumulate grime faster than most British cities. The Underground throws fine soot into the air, central heating pulls dust through every radiator vent, and hard London water leaves a chalky limescale layer over taps, kettles and shower screens within weeks of a regular clean. Our one off deep cleaning in London is the periodic reset that pulls all of that buildup out of the property in a single visit."),
        T("overview_p2", "Paragraph 2", "textarea", "We are not a recurring weekly cleaner — we are a specialist deep cleaning company. Every visit is staffed by a small team so a typical home is finished in one day rather than spread over multiple light visits. Tenants book us for spring cleans and pre-Christmas resets; landlords use us mid-tenancy and during gaps between long-term lets; and homeowners book us after building works, before listing the property for sale, or after a particularly dusty winter."),
        T("overview_p3", "Paragraph 3", "textarea", "If your booking is tied to a move-out date, our specialist end of tenancy cleaning in London is a better fit — it adds the inventory-specific items a checkout report demands. For everything else, a one-off deep clean is the cleanest, most cost-effective way to reset a London home."),
      ]},
      { label: "Checklist Section", fields: [
        T("checklist_eyebrow",  "Eyebrow",    "text",     "What's Included"),
        T("checklist_heading",  "Heading",    "text",     "A 4-zone professional deep clean specification"),
        T("checklist_sub",      "Subheading", "textarea", "Each zone is treated with a different method and chemistry, because greasy kitchens, mineral-heavy bathrooms, fabric-rich bedrooms and high-touch hallways all wear differently in a London home."),
      ]},
      { label: "Why Choose Us", fields: [
        T("why_eyebrow",  "Eyebrow",    "text",     "Why Book Us"),
        T("why_heading",  "Heading",    "text",     "London's most thorough one off deep cleaners"),
        T("why_sub",      "Subheading", "textarea", "A deep clean is only worth booking if it actually lasts. The way we work — top-down, outside-in, surface-specific chemistry — is why our clients re-book us six months later rather than monthly."),
        T("why1_title","Card 1 title","text",     "Top-to-bottom specification"),
        T("why1_desc", "Card 1 desc", "textarea", "We work ceiling-down and outside-in so dust falls onto surfaces we have not yet touched — a level of method most one-off cleaners skip."),
        T("why2_title","Card 2 title","text",     "Honest, fixed pricing"),
        T("why2_desc", "Card 2 desc", "textarea", "A flat all-in quote based on bedrooms and condition. No surprise add-ons for limescale, grease or pet hair on the day."),
        T("why3_title","Card 3 title","text",     "Post-renovation ready"),
        T("why3_desc", "Card 3 desc", "textarea", "After-builders dust, plaster residue and paint flecks need specialist removal — our deep clean teams are trained on post-construction work."),
        T("why4_title","Card 4 title","text",     "Allergy-conscious products"),
        T("why4_desc", "Card 4 desc", "textarea", "HEPA vacuums, fragrance-free degreasers and microfibre-only cloths reduce dust mite, pet dander and pollen load for sensitive households."),
        T("why5_title","Card 5 title","text",     "Air-quality focus"),
        T("why5_desc", "Card 5 desc", "textarea", "We dust ceiling vents, extractor grilles and radiator backs — the surfaces that recycle dust back into the air every time the heating kicks on."),
        T("why6_title","Card 6 title","text",     "Flexible scheduling"),
        T("why6_desc", "Card 6 desc", "textarea", "Evening, weekend and split-session slots so a full deep clean does not require you to take a day off work."),
      ]},
      { label: "Process Steps", fields: [
        T("process_eyebrow",  "Eyebrow",    "text",     "The Process"),
        T("process_heading",  "Heading",    "text",     "How a Prime Tenancy deep clean runs"),
        T("process_sub",      "Subheading", "textarea", "From the moment you message us to the final walk-through, the entire booking takes about a week — and the clean itself is done in a single day."),
        T("step1_title","Step 1 title","text",     "Walkthrough quote"),
        T("step1_desc", "Step 1 desc", "textarea", "Share the property size, the rooms that need the most attention and any condition notes. We send a fixed quote within hours."),
        T("step2_title","Step 2 title","text",     "Pre-clean prep"),
        T("step2_desc", "Step 2 desc", "textarea", "On the day, we move light furniture aside, set up HEPA vacuums and prepare each room with the correct chemicals for its surfaces."),
        T("step3_title","Step 3 title","text",     "Detail clean"),
        T("step3_desc", "Step 3 desc", "textarea", "Top-down, outside-in. We work room by room with two to three cleaners so every surface is touched without rushing the detail work."),
        T("step4_title","Step 4 title","text",     "Walk and review"),
        T("step4_desc", "Step 4 desc", "textarea", "We walk the property with you and refresh anything that does not meet the standard before we leave. No bills until you are happy."),
      ]},
      { label: "When to Book Section", fields: [
        T("spec_eyebrow",  "Eyebrow",        "text",     "When to book"),
        T("spec_heading",  "Heading",        "text",     "The right moment for a London deep clean"),
        T("spec_p1",       "Paragraph 1",    "textarea", "A deep clean is a periodic investment, not a recurring service. Most Londoners get the most value by booking around a specific trigger: a season change, a renovation, a house-move, or simply a year of regular cleans piling up under the radar. We adjust the chemistry and the team size based on the trigger so the booking is right-sized."),
        T("spec_p2",       "Paragraph 2",    "textarea", "If you live with pets or allergies in a flat with carpets, pair the deep clean with our carpet cleaning service in London for an even bigger reset."),
        T("spec_sidebar",  "Sidebar heading","text",     "Popular booking triggers"),
      ]},
      { label: "FAQ Section", fields: [
        T("faq_heading","Section heading",    "text",     "Deep cleaning London FAQs"),
        T("faq_sub",    "Section subheading", "textarea", "What clients ask before booking a one-off professional deep clean."),
        T("faq1_q","FAQ 1 question","text",     "What is the difference between deep cleaning and regular cleaning in London?"),
        T("faq1_a","FAQ 1 answer",  "textarea", "A regular clean handles visible surfaces — counters, floors, bathrooms — on a recurring weekly or fortnightly basis. A deep clean in London goes after the build-up regular cleans leave behind: scale around taps and tile grout, grease behind the cooker, dust on top of cabinets, marks on skirting boards, soap residue in shower screens and inside-cupboard wipe-downs. It is a longer, slower, more detailed job that resets the entire property."),
        T("faq2_q","FAQ 2 question","text",     "How often should I book a one-off deep clean in London?"),
        T("faq2_a","FAQ 2 answer",  "textarea", "Most London residents book a one-off deep clean every six to twelve months, plus an extra deep clean after building works, after a long winter, before hosting guests for the holidays, or before a property valuation."),
        T("faq3_q","FAQ 3 question","text",     "How long does professional deep cleaning take in London?"),
        T("faq3_a","FAQ 3 answer",  "textarea", "A studio takes 4 to 5 hours, a one-bedroom flat 5 to 7 hours, and larger family homes can be a full day with a team of three cleaners."),
        T("faq4_q","FAQ 4 question","text",     "Do professional deep cleaners bring their own equipment?"),
        T("faq4_a","FAQ 4 answer",  "textarea", "Yes. Our deep cleaning teams arrive with hospital-grade degreasers, descalers, microfibre cloth packs, HEPA vacuums, scale-removing scrub pads, telescopic dusters for ceilings and cornices, and steam machines for grout. You do not need to provide anything beyond water and electricity."),
        T("faq5_q","FAQ 5 question","text",     "Can deep cleaning help if I have asthma or allergies in London?"),
        T("faq5_a","FAQ 5 answer",  "textarea", "Yes. A proper deep clean removes the dust mites, pet dander, pollen residue and mould spores that build up in soft furnishings, skirting and behind furniture. We use fragrance-free, asthma-friendly products on request and HEPA-filter vacuums so we capture allergens rather than redistribute them."),
        T("faq6_q","FAQ 6 question","text",     "Is a deep clean enough for end of tenancy or do I need a separate service?"),
        T("faq6_a","FAQ 6 answer",  "textarea", "A deep clean covers most of what an inventory check looks for, but a checkout report often includes specifics — like the inside of every cupboard, the freezer compartment defrosted, and the oven dip-tanked — that we separate into a dedicated end of tenancy clean."),
      ]},
      { label: "CTA", fields: [
        T("cta_title", "CTA title",       "text",     "Book your London deep clean"),
        T("cta_desc",  "CTA description", "textarea", "Fixed pricing, allergy-friendly products and a one-day finish for most flats. Get your quote now."),
      ]},
    ],
  },
  {
    key: "carpet", label: "Carpet Cleaning", path: "/carpet-cleaning-london",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "Carpet Cleaning London"),
        T("h1",         "H1 title",        "text",     "Professional Steam Carpet Cleaning"),
        T("highlight",  "H1 gold word",    "text",     "Across London"),
        T("subheading", "Hero subheading", "textarea", "Hot-water extraction that lifts ground-in dirt, pet odours, allergens and stains — fast-drying, inventory-safe and gentle enough for wool carpets."),
      ]},
      { label: "Overview Section", fields: [
        T("overview_eyebrow",  "Eyebrow",    "text",     "Service Overview"),
        T("overview_heading",  "Heading",    "text",     "Steam carpet cleaning London tenants and landlords actually trust"),
        T("overview_p1", "Paragraph 1", "textarea", "A London carpet sees more wear than almost any other surface in the home. Shoe traffic from pavements and the Tube, urban soot drifting through windows, pet hair, food crumbs and the inevitable wine spill mean even brand-new carpets look tired inside twelve months. A professional steam carpet cleaning visit pulls all of that buildup out of the pile in a single appointment and resets the carpet to the closest possible state to its original colour and texture."),
        T("overview_p2", "Paragraph 2", "textarea", "Our method is hot-water extraction — the technique manufacturers recommend and the only carpet cleaning method accepted by most inventory clerks for an end of tenancy carpet cleaning sign-off. Hot, detergent-infused water is injected into the carpet at pressure, then immediately extracted along with the dissolved soil. The fibres are left clean rather than coated, and drying time is typically two to four hours."),
        T("overview_p3", "Paragraph 3", "textarea", "Whether you are a tenant booking a final clean to recover your deposit, a landlord refreshing a flat between tenancies, or a homeowner tackling years of foot traffic, the booking process is the same — and we coordinate with your wider clean."),
      ]},
      { label: "Checklist Section", fields: [
        T("checklist_eyebrow",  "Eyebrow",    "text",     "What's Included"),
        T("checklist_heading",  "Heading",    "text",     "The three stages of a professional carpet clean"),
        T("checklist_sub",      "Subheading", "textarea", "A proper steam clean is not just about the machine. The pre-treatment, the extraction technique and the finish all influence the final result."),
      ]},
      { label: "Why Choose Us", fields: [
        T("why_eyebrow",  "Eyebrow",    "text",     "Why Book Us"),
        T("why_heading",  "Heading",    "text",     "Why London chooses Prime Tenancy for carpets"),
        T("why_sub",      "Subheading", "textarea", "A carpet clean should outlast the visit by months. The detail we apply pre-extraction is what makes the result stick."),
        T("why1_title","Card 1 title","text",     "Steam carpet cleaning London"),
        T("why1_desc", "Card 1 desc", "textarea", "Hot-water extraction at professional pressure and temperature — the deep-clean method specified by carpet manufacturers and recommended in tenancy inventories."),
        T("why2_title","Card 2 title","text",     "Fast 2–4 hour drying"),
        T("why2_desc", "Card 2 desc", "textarea", "Industrial-grade extractors and optional drying fans mean carpets are usable again within hours, not days."),
        T("why3_title","Card 3 title","text",     "Inventory & deposit safe"),
        T("why3_desc", "Card 3 desc", "textarea", "Our cleaning is documented to the standard letting agents expect on a checkout report — perfect alongside an end of tenancy clean."),
        T("why4_title","Card 4 title","text",     "Pet stain & odour specialists"),
        T("why4_desc", "Card 4 desc", "textarea", "Enzymatic pre-treatments and sub-surface injection for deeply set pet accidents — odour neutralisation, not masking."),
        T("why5_title","Card 5 title","text",     "Stain treatment expertise"),
        T("why5_desc", "Card 5 desc", "textarea", "Coffee, wine, ink, makeup, candle wax and curry. We carry a spotting kit on every visit and treat stains before extraction."),
        T("why6_title","Card 6 title","text",     "Same-day & weekend slots"),
        T("why6_desc", "Card 6 desc", "textarea", "Carpet cleaning is rarely planned in advance. We routinely fit in next-day jobs and run weekend appointments year-round."),
      ]},
      { label: "Process Steps", fields: [
        T("process_eyebrow",  "Eyebrow", "text", "The Process"),
        T("process_heading",  "Heading", "text", "From booking to back in your living room — four steps"),
        T("step1_title","Step 1 title","text",     "Quote in minutes"),
        T("step1_desc", "Step 1 desc", "textarea", "Tell us how many rooms or square metres of carpet, plus your London postcode. We price per room with optional add-ons like rugs and stairs."),
        T("step2_title","Step 2 title","text",     "Inspect & pre-treat"),
        T("step2_desc", "Step 2 desc", "textarea", "On arrival we vacuum, identify stains, apply enzyme pre-treatments and prepare the room for extraction."),
        T("step3_title","Step 3 title","text",     "Steam extract"),
        T("step3_desc", "Step 3 desc", "textarea", "Hot-water extraction with professional equipment — typically a two-pass clean to fully flush the pile."),
        T("step4_title","Step 4 title","text",     "Groom & finish"),
        T("step4_desc", "Step 4 desc", "textarea", "We groom the pile, place foam blocks under any furniture being replaced, and walk through with you before we leave."),
      ]},
      { label: "Checkout / Specifics", fields: [
        T("spec_eyebrow",  "Eyebrow",        "text",     "End of tenancy carpet cleaning"),
        T("spec_heading",  "Heading",        "text",     "The carpet clean inventory clerks look for at checkout"),
        T("spec_p1",       "Paragraph 1",    "textarea", "\"Professionally cleaned\" is the wording most London tenancy agreements use, and it has a specific meaning: hot-water extraction by a third-party contractor, with a receipt. A vacuum and a stain spot-treatment will not satisfy a checkout report. We issue a dated invoice on company letterhead that you can hand straight to your letting agent as evidence of the clean."),
        T("spec_p2",       "Paragraph 2",    "textarea", "If you book end of tenancy carpet cleaning alongside the full inventory clean, our team coordinates the visit so the carpets are completed last and dry by the time the inventory clerk arrives."),
        T("spec_sidebar",  "Sidebar heading","text",     "Stains we tackle every week"),
      ]},
      { label: "FAQ Section", fields: [
        T("faq_heading","Section heading",    "text",     "Carpet cleaning London FAQs"),
        T("faq_sub",    "Section subheading", "textarea", "The questions tenants and landlords ask before they book."),
        T("faq1_q","FAQ 1 question","text",     "What is the best carpet cleaning method for London homes — steam or dry?"),
        T("faq1_a","FAQ 1 answer",  "textarea", "For most London homes we recommend hot-water extraction, often called steam carpet cleaning. It is the only method that flushes ground-in soil out of the carpet pile rather than just lifting the surface."),
        T("faq2_q","FAQ 2 question","text",     "How long does carpet cleaning take to dry in London?"),
        T("faq2_a","FAQ 2 answer",  "textarea", "Most carpets are dry to the touch within 2 to 4 hours and fully dry within 6 to 8 hours. We use professional truck-mount and portable extractors that pull more moisture back out of the pile than retail machines, so drying time is significantly shorter than DIY rentals."),
        T("faq3_q","FAQ 3 question","text",     "Will end of tenancy carpet cleaning remove pet stains and odours?"),
        T("faq3_a","FAQ 3 answer",  "textarea", "Yes, in almost all cases. We treat urine, vomit and mud stains with enzymatic pre-treatments before extraction, which breaks down the proteins causing the odour rather than just masking the smell."),
        T("faq4_q","FAQ 4 question","text",     "How often should I have my London carpets professionally cleaned?"),
        T("faq4_a","FAQ 4 answer",  "textarea", "For a typical London household we recommend once every 12 to 18 months. Homes with pets, allergies, smokers or heavy foot traffic benefit from every 6 to 9 months."),
        T("faq5_q","FAQ 5 question","text",     "Do you provide carpet cleaning to landlords and letting agents?"),
        T("faq5_a","FAQ 5 answer",  "textarea", "Yes — landlords and London letting agents make up a large portion of our carpet cleaning bookings. We are familiar with AIIC inventory expectations, work to checkout-report standards, and can issue agent-friendly invoices."),
        T("faq6_q","FAQ 6 question","text",     "Can you clean rugs, upholstery and stairs alongside the main carpets?"),
        T("faq6_a","FAQ 6 answer",  "textarea", "Yes. Stair carpets, area rugs, fabric sofas and dining chairs can be cleaned in the same visit. We pre-test colour-fast safe materials before extraction and adjust the method for delicate fabrics such as wool blends or silk blends."),
      ]},
      { label: "CTA", fields: [
        T("cta_title", "CTA title",       "text",     "Book carpet cleaning in London"),
        T("cta_desc",  "CTA description", "textarea", "Fixed per-room pricing, fast 2-4 hour drying and weekend slots. Get your quote now."),
      ]},
    ],
  },
  {
    key: "oven", label: "Oven Cleaning", path: "/oven-cleaning-london",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "Oven Cleaning London"),
        T("h1",         "H1 title",        "text",     "Professional Oven Cleaning"),
        T("highlight",  "H1 gold word",    "text",     "in London"),
        T("subheading", "Hero subheading", "textarea", "Dip-tank degreasing for single, double and range ovens, hobs, extractors and microwaves — restored to a like-new finish in a single visit."),
      ]},
      { label: "Overview Section", fields: [
        T("overview_eyebrow",  "Eyebrow",    "text",     "Service Overview"),
        T("overview_heading",  "Heading",    "text",     "The London oven clean that actually clears a checkout report"),
        T("overview_p1", "Paragraph 1", "textarea", "The oven is the single most-photographed appliance on a London inventory report. Every inventory clerk knows that a tenant has often spent ten minutes wiping the door glass and an hour avoiding the back panel — and that is exactly what they photograph at check-in and check-out. A professional oven cleaner does the job a kitchen sponge cannot: removes the parts, takes them outside the property, and soaks them clean rather than scrubbing into a flat surface."),
        T("overview_p2", "Paragraph 2", "textarea", "Our process is built around a heated dip-tank. Racks, side liners, fan covers and the inner door glass are removed and soaked in a non-caustic, low-fume degreaser away from your kitchen. While they soak, our cleaner hand-treats the cavity, fan housing, oven floor and door seal inside the kitchen. Parts are rinsed, dried, refitted and polished — and the finished oven is safe to use again within minutes because no caustic residue is ever applied inside the appliance."),
        T("overview_p3", "Paragraph 3", "textarea", "We clean every type of oven sold in the UK, from budget-built electric singles up to AGA ranges, Neff Slide&Hide pyrolytic models and integrated Smeg dual-fuel ranges. If your booking is part of a wider checkout, pair this service with our specialist end of tenancy cleaning in London so a single team handles the whole inventory checklist."),
      ]},
      { label: "Checklist Section", fields: [
        T("checklist_eyebrow",  "Eyebrow",    "text",     "What's Included"),
        T("checklist_heading",  "Heading",    "text",     "The full London oven cleaning specification"),
        T("checklist_sub",      "Subheading", "textarea", "Every appliance is priced individually. Most checkout bookings combine the oven, hob and extractor — together that is everything an inventory clerk inspects in detail."),
      ]},
      { label: "Why Choose Us", fields: [
        T("why_eyebrow",  "Eyebrow",    "text",     "Why Choose Us"),
        T("why_heading",  "Heading",    "text",     "London's most reliable professional oven cleaners"),
        T("why_sub",      "Subheading", "textarea", "The right chemistry on the right oven type, applied with the right process — that's what separates a restored oven from a wiped one."),
        T("why1_title","Card 1 title","text",     "Dip-tank deep clean"),
        T("why1_desc", "Card 1 desc", "textarea", "We do not just spray and wipe. Racks, side liners and door glass are removed and soaked in our heated dip-tank for a true restoration finish."),
        T("why2_title","Card 2 title","text",     "Like-new finish"),
        T("why2_desc", "Card 2 desc", "textarea", "The back panel, fan housing, door glass interior and seal are all restored — the parts inventory clerks photograph during a checkout report."),
        T("why3_title","Card 3 title","text",     "Pyrolytic & AGA safe"),
        T("why3_desc", "Card 3 desc", "textarea", "Trained on Neff Slide&Hide, AGA, Rayburn, Smeg ranges and modern pyrolytic / catalytic ovens — no caustic damage, no warranty issues."),
        T("why4_title","Card 4 title","text",     "Non-caustic, low-fume"),
        T("why4_desc", "Card 4 desc", "textarea", "Biodegradable chemistry that is safe for kitchens shared with babies, toddlers, pets and households sensitive to harsh fumes."),
        T("why5_title","Card 5 title","text",     "Fully insured cleaners"),
        T("why5_desc", "Card 5 desc", "textarea", "£5M public liability cover, DBS-vetted staff and floor protection used on every job — peace of mind for tenants and landlords alike."),
        T("why6_title","Card 6 title","text",     "Same-day move-out slots"),
        T("why6_desc", "Card 6 desc", "textarea", "Tied to a London checkout? We routinely fit in oven-only jobs the same day or evening before the inventory clerk arrives."),
      ]},
      { label: "Process Steps", fields: [
        T("process_eyebrow",  "Eyebrow", "text", "The Process"),
        T("process_heading",  "Heading", "text", "From the doormat to a sparkling oven in four steps"),
        T("step1_title","Step 1 title","text",     "Quick quote"),
        T("step1_desc", "Step 1 desc", "textarea", "Tell us your appliances and postcode. Each oven, hob and extractor is priced separately so you only pay for what your tenancy needs."),
        T("step2_title","Step 2 title","text",     "Floor protection"),
        T("step2_desc", "Step 2 desc", "textarea", "On arrival we lay protective sheeting under the oven and across the floor path. No grease or product touches your kitchen surfaces."),
        T("step3_title","Step 3 title","text",     "Dip-tank degrease"),
        T("step3_desc", "Step 3 desc", "textarea", "All removable parts are taken to our outside-property dip-tank while the cavity is hand-cleaned inside the kitchen."),
        T("step4_title","Step 4 title","text",     "Re-assemble & polish"),
        T("step4_desc", "Step 4 desc", "textarea", "Every part is rinsed, dried and refitted. We test the door seal, polish the exterior and walk you through the result before we leave."),
      ]},
      { label: "Move-out / Specifics", fields: [
        T("spec_eyebrow",  "Eyebrow",        "text",     "Move out oven cleaning"),
        T("spec_heading",  "Heading",        "text",     "The deposit-saver for London tenants"),
        T("spec_p1",       "Paragraph 1",    "textarea", "If we had to pick one item that most often costs London tenants part of their deposit, it would be the oven. It is also the single most fixable issue with a professional visit. A dedicated oven clean usually costs less than a typical deposit deduction for a \"dirty oven\" line item — so the booking pays for itself even on a worst-case checkout."),
        T("spec_p2",       "Paragraph 2",    "textarea", "For a complete move-out, combine oven cleaning with our carpet cleaning in London and the full end of tenancy clean — that bundle covers every inventory line a London letting agent will pull you up on."),
        T("spec_sidebar",  "Sidebar heading","text",     "Appliances we clean every week"),
      ]},
      { label: "FAQ Section", fields: [
        T("faq_heading","Section heading",    "text",     "Oven cleaning London FAQs"),
        T("faq_sub",    "Section subheading", "textarea", "Frequently asked questions about professional oven cleaning in London."),
        T("faq1_q","FAQ 1 question","text",     "How does professional oven cleaning in London differ from DIY?"),
        T("faq1_a","FAQ 1 answer",  "textarea", "DIY oven cleaning relies on caustic supermarket sprays applied while the oven is in place. Professional oven cleaning uses a heated dip-tank: we remove the racks, side liners, fan covers and door glass, soak them in a non-caustic degreaser away from the property, then hand-clean the cavity in place. The result is restored to near-new condition with no chemical residue inside the oven."),
        T("faq2_q","FAQ 2 question","text",     "How long does a professional oven clean take in a London flat?"),
        T("faq2_a","FAQ 2 answer",  "textarea", "A single oven typically takes 90 to 120 minutes, a double oven 2 to 3 hours, and a range cooker 3 to 4 hours. Hobs, extractors and microwaves can be added to the same visit and are usually completed inside an extra hour each."),
        T("faq3_q","FAQ 3 question","text",     "Do I need professional oven cleaning before moving out of my London rental?"),
        T("faq3_a","FAQ 3 answer",  "textarea", "Yes — and this is the single most common deposit deduction trigger we see. A clean oven is the first item most letting agents check on the inventory checkout report, and the back panel, fan housing and door glass interior are almost impossible to clean to inventory standard without dip-tank degreasing."),
        T("faq4_q","FAQ 4 question","text",     "Can you clean self-cleaning, pyrolytic and catalytic ovens safely?"),
        T("faq4_a","FAQ 4 answer",  "textarea", "Yes. We do not use caustic chemicals inside the cavity of pyrolytic or catalytic ovens — caustic products damage the special enamel that makes those systems work. Our process is dip-tank based for removable parts and uses non-caustic degreaser inside the cavity."),
        T("faq5_q","FAQ 5 question","text",     "Are the products you use safe for children and pets?"),
        T("faq5_a","FAQ 5 answer",  "textarea", "Yes. Our dip-tank chemistry is non-caustic, low-fume and biodegradable. Inside the property we use plant-based degreasers that leave no toxic residue, so the oven is safe to use within minutes of completion — including for households with babies and pets."),
        T("faq6_q","FAQ 6 question","text",     "Will move out oven cleaning include the hob and extractor as well?"),
        T("faq6_a","FAQ 6 answer",  "textarea", "We price each appliance separately so you only pay for what your tenancy needs. The most common booking for a checkout is oven + hob + extractor filter, which together cover every appliance an inventory clerk inspects in detail."),
      ]},
      { label: "CTA", fields: [
        T("cta_title", "CTA title",       "text",     "Book oven cleaning in London"),
        T("cta_desc",  "CTA description", "textarea", "Fixed per-appliance pricing, dip-tank deep clean and non-caustic chemistry. Same-day and weekend slots available."),
      ]},
    ],
  },
  {
    key: "prices", label: "Prices", path: "/prices",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "Transparent Pricing"),
        T("h1",         "H1 title",        "text",     "Transparent End of Tenancy Cleaning Prices"),
        T("highlight",  "H1 gold word",    "text",     "in London"),
        T("subheading", "Hero subheading", "textarea", "Fixed quotes — no hidden fees, no surprise add-ons. Landlord and estate-agent approved cleaning backed by our 7-day re-clean guarantee."),
      ]},
      { label: "Prices", fields: [
        T("studio",   "Studio (£)",     "number", 99),
        T("oneBed",   "1 Bedroom (£)",  "number", 149),
        T("twoBed",   "2 Bedroom (£)",  "number", 179),
        T("threeBed", "3 Bedroom (£)",  "number", 230),
        T("fourBed",  "4+ Bedroom (£)", "number", 299),
      ]},
      { label: "Packages Section", fields: [
        T("pkgs_eyebrow",  "Eyebrow",    "text",     "Cleaning Packages"),
        T("pkgs_heading",  "Heading",    "text",     "Pick the package that fits your London property"),
        T("pkgs_sub",      "Subheading", "textarea", "Every package is inventory-grade by default — the only difference is property size and the time on site. Add carpet cleaning, oven cleaning or upholstery work below to build the right bundle for your checkout."),
      ]},
      { label: "What's Included", fields: [
        T("inc_eyebrow",  "Eyebrow",    "text",     "What's included"),
        T("inc_heading",  "Heading",    "text",     "Every package covers the full inventory checklist"),
        T("inc_sub",      "Subheading", "textarea", "Six zones, every surface — the same specification a London inventory clerk inspects against during a checkout report."),
      ]},
      { label: "Add-ons Section", fields: [
        T("addons_eyebrow",  "Eyebrow",    "text",     "Optional Add-ons"),
        T("addons_heading",  "Heading",    "text",     "Pay only for the extras you actually need"),
        T("addons_sub",      "Subheading", "textarea", "Bundle two or more add-ons with an end of tenancy package and your quote includes an automatic discount on each. Mix and match — every extra is priced clearly below."),
      ]},
      { label: "Why Choose Us", fields: [
        T("why_eyebrow",  "Eyebrow",    "text",     "Why London chooses us"),
        T("why_heading",  "Heading",    "text",     "Premium cleaning, priced fairly"),
        T("why_sub",      "Subheading", "textarea", "The reasons clients re-book Prime Tenancy and recommend us to their letting agents."),
        T("why1_title","Card 1 title","text",     "£5M insured cleaners"),
        T("why1_desc", "Card 1 desc", "textarea", "Public liability cover on every visit — peace of mind for landlords and tenants alike."),
        T("why2_title","Card 2 title","text",     "Flexible 7-day booking"),
        T("why2_desc", "Card 2 desc", "textarea", "Online booking with live availability — same-day slots subject to capacity at month-end."),
        T("why3_title","Card 3 title","text",     "Weekend appointments"),
        T("why3_desc", "Card 3 desc", "textarea", "Saturday and Sunday slots at no premium. Bank holidays bookable on request."),
        T("why4_title","Card 4 title","text",     "Eco-friendly products"),
        T("why4_desc", "Card 4 desc", "textarea", "Plant-based, fragrance-free chemistry as standard. Safe for kids, pets and sensitive landlords."),
        T("why5_title","Card 5 title","text",     "Estate-agent standards"),
        T("why5_desc", "Card 5 desc", "textarea", "Cleaning specified against the AIIC inventory benchmark used by major London letting agents."),
        T("why6_title","Card 6 title","text",     "Re-clean focused"),
        T("why6_desc", "Card 6 desc", "textarea", "7-day re-clean guarantee if your landlord flags any cleaning issue on the checkout report."),
      ]},
      { label: "FAQ Section", fields: [
        T("faq_heading","Section heading",    "text",     "End of tenancy cleaning prices — your questions answered"),
        T("faq_sub",    "Section subheading", "textarea", "The most asked pricing questions from London tenants, landlords and letting agents."),
        T("faq1_q","FAQ 1 question","text",     "How much does end of tenancy cleaning cost in London?"),
        T("faq1_a","FAQ 1 answer",  "textarea", "End of tenancy cleaning prices in London start from £99 for a studio flat, £149 for a one-bedroom, £179 for a two-bedroom, £230 for a three-bedroom and £299 for four-bedroom homes and larger."),
        T("faq2_q","FAQ 2 question","text",     "Is carpet cleaning included in the end of tenancy clean?"),
        T("faq2_a","FAQ 2 answer",  "textarea", "Carpet steam cleaning is priced separately as an add-on (from £25 per room) because not every tenancy requires it. If your tenancy agreement specifies professionally cleaned carpets, or your carpets are visibly soiled, we strongly recommend adding it."),
        T("faq3_q","FAQ 3 question","text",     "Do you guarantee deposit return?"),
        T("faq3_a","FAQ 3 answer",  "textarea", "No professional cleaner can sign your landlord's deposit decision for them — but we guarantee the cleaning standard. Every end of tenancy job comes with a written 7-day re-clean guarantee."),
        T("faq4_q","FAQ 4 question","text",     "How long does end of tenancy cleaning take in London?"),
        T("faq4_a","FAQ 4 answer",  "textarea", "A studio takes around 3 to 4 hours, a one-bedroom flat 4 to 5 hours, a two-bedroom property 5 to 7 hours, a three-bedroom home 7 to 9 hours, and four-bedroom houses 9 to 12 hours."),
        T("faq5_q","FAQ 5 question","text",     "Are cleaning supplies and equipment included?"),
        T("faq5_a","FAQ 5 answer",  "textarea", "Yes — every quote is fully inclusive. Our cleaners arrive with professional-grade equipment (HEPA vacuums, dip-tanks for oven cleaning, hot-water carpet extractors), all the chemistry needed for the job and microfibre kits."),
        T("faq6_q","FAQ 6 question","text",     "Are there any hidden fees?"),
        T("faq6_a","FAQ 6 answer",  "textarea", "No. The price you see on our quote is the price you pay. No call-out fees, no on-the-day surprise charges, no congestion-charge add-ons, no parking surcharges unless explicitly flagged at quote time."),
        T("faq7_q","FAQ 7 question","text",     "Is your end of tenancy cleaning estate-agent approved?"),
        T("faq7_a","FAQ 7 answer",  "textarea", "Yes. Our cleaning specification mirrors the AIIC inventory benchmark used by the major London letting agents — Foxtons, Hamptons, Knight Frank, Dexters, Chestertons and most independents."),
      ]},
    ],
  },
  {
    key: "about", label: "About", path: "/about",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "About Us"),
        T("h1",         "H1 title",        "text",     "The London cleaning team built"),
        T("highlight",  "H1 gold word",    "text",     "around the inventory report"),
        T("subheading", "Hero subheading", "textarea", "Prime Tenancy Cleaning was founded to fix one specific problem: the gap between a \"domestic\" clean and what a London letting agent actually inspects against."),
      ]},
      { label: "Story Section", fields: [
        T("story_eyebrow",  "Eyebrow", "text", "Our Story"),
        T("story_heading",  "Heading", "text", "A cleaning company for London's rental market — not the other way around"),
        T("story_p1", "Paragraph 1", "textarea", "Prime Tenancy Cleaning was started by a team with first-hand experience of London's rental cycle on both sides: as long-term tenants who lost deposits to checkout reports that felt unfair, and later as landlords frustrated by contractors who said the right things but did not deliver inventory-grade work."),
        T("story_p2", "Paragraph 2", "textarea", "We built the company we wished we had been able to book. A small, employed cleaning team trained specifically on the AIIC inventory standard, dispatched from a single London base, using the same checklist used by major letting agents to evaluate checkout reports. No franchise patchwork, no last-minute subcontractors, no shifting spec."),
        T("story_p3", "Paragraph 3", "textarea", "Today we serve tenants, landlords and letting agents across all 32 London boroughs. Most of our bookings come from agent referrals and word-of-mouth from tenants who recovered their full deposit on the first attempt."),
      ]},
      { label: "Values Section", fields: [
        T("values_eyebrow",  "Eyebrow",    "text",     "What We Stand For"),
        T("values_heading",  "Heading",    "text",     "The six principles every job runs on"),
        T("values_sub",      "Subheading", "textarea", "No marketing-speak. These are the operating rules our team actually works to, every single visit."),
      ]},
      { label: "Coverage Section", fields: [
        T("coverage_eyebrow",  "Eyebrow", "text",     "Where We Work"),
        T("coverage_heading",  "Heading", "text",     "All 32 London boroughs, Zone 1 to the M25"),
        T("coverage_body",     "Body",    "textarea", "From Mayfair mansion blocks to Walthamstow HMOs, Greenwich townhouses to Wandsworth conversions — our teams cover every London postcode. Same-day jobs are available across central zones, with planned bookings in outer boroughs from £99."),
      ]},
      { label: "CTA", fields: [
        T("cta_title", "CTA title",       "text",     "Work with a London team built for inventories"),
        T("cta_desc",  "CTA description", "textarea", "Get a tailored quote in hours — or browse our service pages to see exactly what each clean includes."),
      ]},
    ],
  },
  {
    key: "faq", label: "FAQ", path: "/faq",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "Help Centre"),
        T("h1",         "H1 title",        "text",     "London Cleaning"),
        T("highlight",  "H1 gold word",    "text",     "Frequently Asked Questions"),
        T("subheading", "Hero subheading", "textarea", "Detailed answers to the questions tenants, landlords and letting agents ask before booking us. Can't find what you need? Reach out via our contact page."),
      ]},
      { label: "FAQ Category Titles", fields: [
        T("cat1_title", "Category 1 title", "text", "Booking & Pricing"),
        T("cat2_title", "Category 2 title", "text", "Service & Standards"),
        T("cat3_title", "Category 3 title", "text", "On the Day"),
        T("cat4_title", "Category 4 title", "text", "End of Tenancy Specifics"),
        T("cat5_title", "Category 5 title", "text", "Carpet, Oven & Add-Ons"),
      ]},
      { label: "Still Have Questions", fields: [
        T("still_heading", "Heading",    "text",     "Still have a question?"),
        T("still_body",    "Body text",  "textarea", "Our London team is happy to walk through your tenancy specifics over the phone or WhatsApp. Most questions are answered in minutes."),
      ]},
    ],
  },
  {
    key: "contact", label: "Contact", path: "/contact",
    sections: [
      { label: "Hero", fields: [
        T("eyebrow",    "Eyebrow",         "text",     "Get In Touch"),
        T("h1",         "H1 title",        "text",     "Request Your"),
        T("highlight",  "H1 gold word",    "text",     "Free London Cleaning Quote"),
        T("subheading", "Hero subheading", "textarea", "Most quotes are sent within a couple of hours. Choose the channel that suits you — phone, WhatsApp, email or the form below."),
      ]},
      { label: "Quote Form Section", fields: [
        T("quote_eyebrow",  "Eyebrow",    "text",     "Request a Quote"),
        T("quote_heading",  "Heading",    "text",     "Tell us about your property"),
        T("quote_sub",      "Subheading", "textarea", "Share your postcode, property size and approximate cleaning date. We'll send a fixed-price quote and propose a slot within a few working hours."),
      ]},
    ],
  },
];

const SitePageEditor = ({ pageCfg, stored, onSave }) => {
  const allDefaults = {};
  pageCfg.sections.forEach(s => s.fields.forEach(f => { allDefaults[f.name] = f.default; }));
  const [form, setForm]     = useState({ ...allDefaults, ...(stored || {}) });
  const [openSec, setOpenSec] = useState(pageCfg.sections[0]?.label || null);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(SITE_CONTENT_ENDPOINT, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Admin-Token": ADMIN_PASSWORD },
        body: JSON.stringify({ page: pageCfg.key, data: form }),
      });
      const d = await res.json();
      if (d.success) { onSave(pageCfg.key, form); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    } catch {}
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-1.5">
      {pageCfg.sections.map(section => (
        <div key={section.label} className="border border-slate-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenSec(openSec === section.label ? null : section.label)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left transition-colors"
          >
            <span className="font-semibold text-xs text-navy uppercase tracking-wider">{section.label}</span>
            <ChevronLeft className={`w-3.5 h-3.5 text-slate-400 transition-transform ${openSec === section.label ? "-rotate-90" : "rotate-180"}`} />
          </button>
          {openSec === section.label && (
            <div className="p-4 space-y-3 border-t border-slate-100">
              {section.fields.map(f => (
                <div key={f.name}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{f.label}</label>
                  <FieldInput field={f} value={form[f.name]} onChange={v => setForm(prev => ({ ...prev, [f.name]: v }))} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={save} disabled={saving}
          className="bg-navy text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
        {saved && <span className="text-green-600 text-sm flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Saved</span>}
        <a href={pageCfg.path} target="_blank" rel="noopener noreferrer"
          className="ml-auto text-xs text-teal hover:text-navy flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> View page
        </a>
      </div>
    </div>
  );
};

const SiteTab = () => {
  const [allContent, setAllContent] = useState({});
  const [open, setOpen]             = useState(null);

  useEffect(() => {
    fetch(SITE_CONTENT_ENDPOINT).then(r => r.json()).then(d => setAllContent(d || {})).catch(() => {});
  }, []);

  const handleSave = (key, data) => setAllContent(prev => ({ ...prev, [key]: data }));

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-bold text-navy">Edit Site Pages</h2>
        <p className="text-slate-500 text-sm mt-0.5">
          Changes apply immediately — no rebuild needed. Expand any page, edit its sections, then Save.
        </p>
      </div>
      <div className="space-y-3">
        {SITE_PAGES.map(pg => (
          <div key={pg.key} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => setOpen(open === pg.key ? null : pg.key)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
            >
              <div>
                <span className="font-semibold text-navy text-sm">{pg.label}</span>
                <span className="ml-3 text-xs font-mono text-slate-400">{pg.path}</span>
              </div>
              <div className="flex items-center gap-2">
                {allContent[pg.key] && (
                  <span className="text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full font-medium">Customised</span>
                )}
                <ChevronLeft className={`w-4 h-4 text-slate-400 transition-transform ${open === pg.key ? "-rotate-90" : "rotate-180"}`} />
              </div>
            </button>
            {open === pg.key && (
              <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                <SitePageEditor pageCfg={pg} stored={allContent[pg.key]} onSave={handleSave} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Plugins Tab ───────────────────────────────────────────────────────────
const PluginsTab = () => (
  <div>
    <div className="mb-6">
      <h2 className="text-lg font-bold text-navy">Plugins & Integrations</h2>
      <p className="text-slate-500 text-sm mt-0.5">
        Active tools and integrations for primetenancycleaning.com
      </p>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {PLUGINS.map((p) => (
        <div key={p.name} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold text-navy">{p.name}</h3>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${p.tagColor}`}>
              {p.tag}
            </span>
          </div>
          <p className="text-slate-500 text-sm leading-relaxed flex-1">{p.description}</p>
          {p.note && (
            <p className="text-xs text-slate-400 italic">{p.note}</p>
          )}
          {p.link && (
            <a
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              className="self-start flex items-center gap-1.5 text-xs font-semibold text-teal hover:text-navy transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open Dashboard
            </a>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ── Upload Section ────────────────────────────────────────────────────────
const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadSection = () => {
  const [dragging, setDragging]     = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [result, setResult]         = useState(null);
  const [images, setImages]         = useState([]);
  const [loadingImgs, setLoading]   = useState(true);
  const fileRef                     = useRef(null);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(IMAGES_ENDPOINT, { headers: { "X-Admin-Token": ADMIN_PASSWORD } });
      const data = await res.json();
      setImages(data.images || []);
    } catch { setImages([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const uploadFile = async (file) => {
    if (!file.type.startsWith("image/")) { setResult({ error: "Images only (JPG, PNG, WebP)." }); return; }
    if (file.size > 5 * 1024 * 1024)    { setResult({ error: "Max file size is 5 MB." }); return; }
    setUploading(true); setResult(null);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const res  = await fetch(UPLOAD_ENDPOINT, { method: "POST", headers: { "X-Admin-Token": ADMIN_PASSWORD }, body: fd });
      const data = await res.json();
      setResult(data);
      if (data.success) fetchImages();
    } catch { setResult({ error: "Upload failed. Ensure the site is deployed." }); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) uploadFile(f); }}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all select-none ${
          dragging ? "border-gold bg-gold/5" : "border-slate-200 hover:border-teal hover:bg-slate-50"
        }`}
      >
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files[0]; if (f) uploadFile(f); e.target.value = ""; }} />
        <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
          {uploading ? <RefreshCw className="w-7 h-7 text-teal animate-spin" /> : <Upload className="w-7 h-7 text-slate-400" />}
        </div>
        <p className="font-semibold text-slate-700 mb-1">{uploading ? "Uploading…" : "Drop an image here or click to browse"}</p>
        <p className="text-sm text-slate-500">JPG, PNG, WebP · max 5 MB</p>
      </div>

      {result && (
        <div className={`rounded-xl p-4 flex items-start gap-3 ${result.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
          {result.success
            ? <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            : <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            {result.success ? (
              <>
                <p className="text-green-800 font-semibold text-sm">Uploaded!</p>
                <p className="text-green-700 text-xs mt-0.5 font-mono break-all">{result.fullUrl}</p>
                <button onClick={() => navigator.clipboard.writeText(result.fullUrl)} className="text-xs text-green-600 underline mt-1">Copy URL</button>
              </>
            ) : <p className="text-red-700 text-sm">{result.error}</p>}
          </div>
          <button onClick={() => setResult(null)}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Site Images</h3>
          <button onClick={fetchImages} className="text-sm text-teal hover:text-navy flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {loadingImgs ? (
          <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
        ) : images.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <FileImage className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No images on the server yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {images.map((img) => (
              <div key={img.filename} className="group relative">
                <div className="aspect-square bg-slate-100 rounded-xl overflow-hidden border border-slate-200">
                  <img src={img.url} alt={img.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy" />
                </div>
                <div className="mt-1.5 px-0.5">
                  <p className="text-xs text-slate-600 truncate font-medium">{img.filename}</p>
                  <p className="text-xs text-slate-400">{formatBytes(img.size)}</p>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${SITE_URL}${img.url}`)}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-700 text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-sm font-medium"
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Analytics Embed ───────────────────────────────────────────────────────
const AnalyticsEmbed = () => {
  const stored = () => localStorage.getItem("ptc_looker_url") || "";
  const [embedUrl, setEmbedUrl] = useState(stored);
  const [input, setInput]       = useState(stored);

  const save = () => { localStorage.setItem("ptc_looker_url", input); setEmbedUrl(input); };
  const clear = () => { localStorage.removeItem("ptc_looker_url"); setEmbedUrl(""); setInput(""); };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
        <strong className="block mb-1">Embed live analytics:</strong>
        <ol className="list-decimal list-inside space-y-0.5 text-blue-700 text-xs">
          <li>Go to <a href="https://lookerstudio.google.com" target="_blank" rel="noopener noreferrer" className="underline">lookerstudio.google.com</a> → create a report</li>
          <li>Connect to GA4 property <strong className="font-mono">{GA4_ID}</strong></li>
          <li>Share → set to "Anyone with link can view"</li>
          <li>File → Embed report → copy the <strong>src URL</strong> from the iframe code</li>
          <li>Paste below and click Save</li>
        </ol>
      </div>
      <div className="flex gap-2">
        <input type="url" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="https://lookerstudio.google.com/embed/reporting/…"
          className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal" />
        <button onClick={save} className="bg-navy text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-navy-light whitespace-nowrap">Save & Load</button>
        {embedUrl && <button onClick={clear} className="border border-slate-200 text-slate-500 px-3 py-2.5 rounded-xl text-sm hover:bg-slate-50"><X className="w-4 h-4" /></button>}
      </div>
      {embedUrl ? (
        <div className="rounded-xl overflow-hidden border border-slate-200">
          <iframe src={embedUrl} title="Looker Studio Analytics" className="w-full" style={{ height: 600, border: "none" }} allowFullScreen />
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-slate-200 h-40 flex items-center justify-center">
          <div className="text-center text-slate-400">
            <BarChart3 className="w-7 h-7 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Paste a Looker Studio embed URL above to see live analytics</p>
          </div>
        </div>
      )}
    </div>
  );
};

const QUICK_TOOLS = [
  { name: "Google Analytics 4", desc: "Traffic & sessions", url: "https://analytics.google.com/analytics/web/", bg: "bg-orange-500", Icon: BarChart3 },
  { name: "Search Console", desc: "Keywords & rankings", url: `https://search.google.com/search-console?resource_id=${encodeURIComponent(SITE_URL)}`, bg: "bg-blue-600", Icon: Search },
  { name: "Jotform", desc: "Quote form inbox", url: "https://www.jotform.com/inbox/", bg: "bg-purple-600", Icon: Globe },
  { name: "Zenbooker", desc: "Booking dashboard", url: "https://www.zenbooker.com/dashboard", bg: "bg-teal", Icon: Settings },
  { name: "Hostinger", desc: "File manager & hosting", url: "https://hpanel.hostinger.com/", bg: "bg-red-600", Icon: Globe },
  { name: "Google Business", desc: "Reviews & maps", url: "https://business.google.com/", bg: "bg-green-600", Icon: Globe },
];

const SEO_CHECKLIST = [
  "Verify site in Google Search Console and submit sitemap.xml",
  "Connect Search Console to GA4 in GA4 → Admin → Search Console links",
  "Create a Looker Studio report for GA4 and paste the embed URL in the Analytics tab",
  "Set up GA4 conversion events: form_submission, phone_click",
  "Create and verify Google Business Profile",
  "Set up Trustpilot and collect first reviews",
  "Build backlinks: London business directories, cleaning industry sites",
  "Monitor keyword rankings weekly via Search Console",
  "Write blog posts targeting borough-specific keywords (e.g. \"end of tenancy cleaning Hackney\")",
];

// ── Dashboard ─────────────────────────────────────────────────────────────
const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("analytics");

  const tabs = [
    { id: "analytics", label: "Analytics",      icon: BarChart3 },
    { id: "posts",     label: "Posts",           icon: FileText  },
    { id: "pages",     label: "Pages",           icon: Layout    },
    { id: "uploads",   label: "Image Uploads",   icon: Image     },
    { id: "site",      label: "Edit Pages",      icon: Globe     },
    { id: "plugins",   label: "Plugins",         icon: Package   },
    { id: "tools",     label: "Quick Links",     icon: Link2     },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy border-b-2 border-gold sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center">
              <Lock className="w-4 h-4 text-navy" />
            </div>
            <div className="leading-none">
              <span className="text-white font-bold text-sm block">PTC Admin</span>
              <span className="text-slate-400 text-xs">primetenancycleaning.com</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+442080363586" className="text-slate-400 hover:text-white text-sm hidden sm:flex items-center gap-1.5">
              <Phone className="w-4 h-4" /> 020 8036 3586
            </a>
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white text-sm flex items-center gap-1.5">
              <ExternalLink className="w-4 h-4" /><span className="hidden sm:inline">Live Site</span>
            </a>
            <button onClick={onLogout} className="text-slate-400 hover:text-white text-sm flex items-center gap-1.5">
              <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Site stats strip */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Pages", value: "47" },
            { label: "Borough Pages", value: "32" },
            { label: "GA4 Property", value: GA4_ID },
            { label: "Domain", value: "primetenancycleaning.com" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">{s.label}</p>
              <p className="font-semibold text-navy text-sm mt-0.5 truncate">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-6 flex min-w-max">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.id ? "border-gold text-navy" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}>
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "analytics" && (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-bold text-navy mb-4">Platform Dashboards</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { name: "Google Analytics 4", desc: "Traffic · Sessions · Conversions", sub: GA4_ID, url: "https://analytics.google.com/analytics/web/", bg: "bg-orange-100", ic: "text-orange-600", Icon: BarChart3 },
                  { name: "Google Search Console", desc: "Keywords · Rankings · Impressions", sub: "primetenancycleaning.com", url: `https://search.google.com/search-console?resource_id=${encodeURIComponent(SITE_URL)}`, bg: "bg-blue-100", ic: "text-blue-600", Icon: Search },
                ].map((card) => (
                  <a key={card.name} href={card.url} target="_blank" rel="noopener noreferrer"
                    className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-teal hover:shadow-sm transition-all group flex items-center gap-4">
                    <div className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <card.Icon className={`w-6 h-6 ${card.ic}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-navy group-hover:text-teal">{card.name}</h3>
                      <p className="text-slate-500 text-sm">{card.desc}</p>
                      <p className="text-xs text-slate-400 mt-0.5 font-mono">{card.sub}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-lg font-bold text-navy mb-4">Live Analytics Embed</h2>
              <div className="bg-white border border-slate-200 rounded-2xl p-6"><AnalyticsEmbed /></div>
            </div>
          </div>
        )}

        {tab === "posts" && <PostsTab />}

        {tab === "pages" && <PagesTab />}

        {tab === "uploads" && (
          <div>
            <div className="mb-6">
              <h2 className="text-lg font-bold text-navy">Image Uploads</h2>
              <p className="text-slate-500 text-sm mt-1">
                Saved to <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">/images/</code> on the live server.
                Works on the live site only — not in local dev.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6"><UploadSection /></div>
          </div>
        )}

        {tab === "site" && <SiteTab />}

        {tab === "plugins" && <PluginsTab />}

        {tab === "tools" && (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-bold text-navy mb-4">Quick Links</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {QUICK_TOOLS.map((t) => (
                  <a key={t.name} href={t.url} target="_blank" rel="noopener noreferrer"
                    className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-teal hover:shadow-sm transition-all group flex items-center gap-4">
                    <div className={`w-11 h-11 ${t.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <t.Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-navy group-hover:text-teal text-sm truncate">{t.name}</h3>
                      <p className="text-slate-500 text-xs truncate">{t.desc}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-teal" />
                  </a>
                ))}
              </div>
            </div>
            <div className="bg-navy rounded-2xl p-6 md:p-8">
              <h2 className="text-lg font-bold text-gold mb-5">SEO Checklist</h2>
              <ul className="space-y-3">
                {SEO_CHECKLIST.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300 text-sm">
                    <span className="w-6 h-6 rounded-full bg-white/10 text-xs font-bold text-gold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────
const Admin = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("ptc_admin_auth") === "1");
  const login  = () => { sessionStorage.setItem("ptc_admin_auth", "1"); setAuthed(true); };
  const logout = () => { sessionStorage.removeItem("ptc_admin_auth"); setAuthed(false); };
  return authed ? <Dashboard onLogout={logout} /> : <LoginScreen onLogin={login} />;
};

export default Admin;
